import React, { useEffect, useMemo, useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { apiActivityLog } from "./api";

// ============================================================
// ActivityLogTab — read-only, append-only audit trail of finance
// approvals, rejections, void requests, and voids.
//
// Backing store: `kc_finance_activity_log` (protected append-only key).
// Data source:   GET /api/finance/activity-log
//
// Filters (client-side after the server-side fetch to keep the UI snappy):
//  - actionType     (dropdown)
//  - performedByName (search)
//  - dateFrom / dateTo (inclusive)
//
// CSV Export button visible to Super Admin AND Limited Admin per user
// spec — finance leaders often hand audit material to external bookkeepers
// without requiring SA involvement.
// ============================================================

const ACTION_TYPES = [
  "Approved",
  "Rejected",
  "Void Requested",
  "Void Approved",
  "Void Rejected",
  "Auto Approved",
];

function fmt(cents, sym = "$") {
  if (!Number.isFinite(cents)) return `${sym}0.00`;
  return `${sym}${(cents / 100).toFixed(2)}`;
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

function actionBadgeColor(action) {
  if (action === "Approved" || action === "Auto Approved") return "#4ade80";
  if (action === "Rejected") return "#f87171";
  if (action === "Void Approved") return "#f97316";
  if (action === "Void Requested") return "var(--kc-accent)";
  if (action === "Void Rejected") return "#a78bfa";
  return "var(--kc-sub)";
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportActivityLogCsv(rows, currencySymbol) {
  const header = [
    "Timestamp",
    "Action",
    "Record Ref",
    "Party (Member / Payee)",
    "Amount",
    "Performed By",
    "Role",
    "Reason",
  ];
  const lines = [header.map(csvEscape).join(",")];
  for (const r of rows) {
    lines.push([
      r.timestamp || "",
      r.actionType || "",
      r.recordRef || "",
      r.partyName || "",
      fmt(r.amountCents || 0, currencySymbol),
      r.performedByName || "",
      r.performedByRole || "",
      r.reason || "",
    ].map(csvEscape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `Kingdom-Connect-Finance-Activity-Log-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 800);
}

export default function ActivityLogTab({ currencySymbol = "$", canExportCsv = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const reload = () => {
    setLoading(true); setError("");
    apiActivityLog()
      .then(list => setEntries(Array.isArray(list) ? list : []))
      .catch(e => setError(e?.message || "Could not load activity log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (actionFilter && e.actionType !== actionFilter) return false;
      if (actorFilter) {
        const needle = actorFilter.toLowerCase();
        const hay = `${e.performedByName || ""} ${e.performedByRole || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (dateFrom && (e.timestamp || "") < dateFrom) return false;
      if (dateTo) {
        const cutoff = `${dateTo}T23:59:59Z`;
        if ((e.timestamp || "") > cutoff) return false;
      }
      return true;
    });
  }, [entries, actionFilter, actorFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setActionFilter("");
    setActorFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const anyFilter = Boolean(actionFilter || actorFilter || dateFrom || dateTo);

  return (
    <div data-testid="finance-activity-log-tab">
      {/* Filters + Export */}
      <div
        style={{
          display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end",
          marginBottom: 14, padding: 12,
          background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)",
          borderRadius: 10,
          border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)",
        }}
      >
        <div style={{ flex: "1 1 140px", minWidth: 140 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Action</label>
          <select
            className="form-input"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            data-testid="finance-activity-filter-action"
          >
            <option value="">All actions</option>
            {ACTION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Performed by</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search name or role…"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            data-testid="finance-activity-filter-actor"
          />
        </div>
        <div style={{ flex: "1 1 140px", minWidth: 130 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Date from</label>
          <input
            type="date"
            className="form-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="finance-activity-filter-from"
          />
        </div>
        <div style={{ flex: "1 1 140px", minWidth: 130 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Date to</label>
          <input
            type="date"
            className="form-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="finance-activity-filter-to"
          />
        </div>
        {anyFilter && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={clearFilters}
            data-testid="finance-activity-filter-clear"
          >Clear</button>
        )}
        <div style={{ flex: 1 }} />
        {canExportCsv && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => exportActivityLogCsv(filtered, currencySymbol)}
            disabled={filtered.length === 0}
            data-testid="finance-activity-export-csv"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              opacity: filtered.length === 0 ? 0.5 : 1,
            }}
            title="Export the currently filtered activity log rows as CSV"
          >
            <CustomIcon name="document" size={12} /> Export CSV
          </button>
        )}
      </div>

      {loading && (
        <div style={{ padding: 20, opacity: 0.6, textAlign: "center" }} data-testid="finance-activity-loading">
          Loading activity log…
        </div>
      )}
      {error && (
        <div
          style={{ padding: 12, background: "rgba(200,50,50,0.15)", color: "#f2a2a2", borderRadius: 8 }}
          data-testid="finance-activity-error"
        >{error}</div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div
          style={{ padding: 20, textAlign: "center", opacity: 0.6 }}
          data-testid="finance-activity-empty"
        >
          {entries.length === 0
            ? "No activity has been recorded yet."
            : "No entries match the current filters."}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          style={{
            overflowX: "auto",
            border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)",
            borderRadius: 10,
          }}
          data-testid="finance-activity-table-wrap"
        >
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
            data-testid="finance-activity-table"
          >
            <thead>
              <tr style={{ background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", color: "var(--kc-accent)" }}>
                <th style={TH}>Timestamp</th>
                <th style={TH}>Action</th>
                <th style={TH}>Record</th>
                <th style={TH}>Party</th>
                <th style={{ ...TH, textAlign: "right" }}>Amount</th>
                <th style={TH}>Performed By</th>
                <th style={TH}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr
                  key={e.entryId}
                  data-testid={`finance-activity-row-${e.entryId}`}
                  style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)" }}
                >
                  <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{fmtTs(e.timestamp)}</td>
                  <td style={TD}>
                    <span
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        background: "color-mix(in srgb, var(--kc-surface) 92%, transparent)",
                        border: `1px solid ${actionBadgeColor(e.actionType)}`,
                        color: actionBadgeColor(e.actionType),
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {e.actionType || "—"}
                    </span>
                  </td>
                  <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{e.recordRef || "—"}</td>
                  <td style={TD}>{e.partyName || "—"}</td>
                  <td style={{ ...TD, textAlign: "right", fontFamily: "monospace", color: "var(--kc-accent)", fontWeight: 700 }}>
                    {fmt(e.amountCents, currencySymbol)}
                  </td>
                  <td style={TD}>
                    {e.performedByName || "—"}
                    {e.performedByRole && (
                      <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.65 }}>
                        · {e.performedByRole}
                      </span>
                    )}
                  </td>
                  <td style={{ ...TD, fontSize: 12, opacity: 0.85, maxWidth: 260 }}>
                    {e.reason ? <span title={e.reason}>{e.reason}</span> : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TH = { textAlign: "left", padding: "10px 12px", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase" };
const TD = { padding: "10px 12px", verticalAlign: "top" };
